import React, { createContext, useState, useEffect } from "react";
import Cookies from "js-cookie";
import {
  useLogin,
  useSignUp,
  useUpdateUser,
  useLoginWithPhone,
  useVerifyPhone,
  useSignUpWithPhone,
  useVerifyPhoneRegister,
} from "../api/useApi";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const loginMutation = useLogin();
  const loginWithPhoneMutation = useLoginWithPhone();
  const verifyPhoneMutation = useVerifyPhone();
  const verifyPhoneRegisterMutation = useVerifyPhoneRegister();
  const signUpMutation = useSignUp();
  const updateUserMutation = useUpdateUser();
  const signUpWithPhoneMutation = useSignUpWithPhone();

  useEffect(() => {
    const userData = Cookies.get("userData");
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const signUp = async (formData) => {
    try {
      // name , phone , password and email
      const { password, name, phone, email } = formData;
      formData = { name, email, password, phone };
      const data = await signUpMutation.mutateAsync(formData);

      if (data.status === "fail") {
        throw new Error(data.message || "Signup failed!");
      }

      return data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong!",
      );
    }
  };

  const logIn = async (email, password) => {
    try {
      const response = await loginMutation.mutateAsync({
        email,
        password,
      });
      const { token, data } = response;
      Cookies.set("authToken", token, { expires: 7 });
      Cookies.set("userData", JSON.stringify(data.user), { expires: 7 });
      setCurrentUser(data.user);
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong!",
      );
    }
  };

  const loginWithPhone = async (phone) => {
    try {
      const response = await loginWithPhoneMutation.mutateAsync(phone);

      return {
        success: true,
        message: response.message,
      };
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong!",
      );
    }
  };

  const signUpWithPhone = async (phone,name,email) => {
    try {
      const response = await signUpWithPhoneMutation.mutateAsync(phone,name,email);
      return {
        success: true,
        message: response.message,
      };
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong!",
      );
    }
  };

  const verifyPhone = async (phone, code) => {
    try {
      const response = await verifyPhoneMutation.mutateAsync({
        phone,
        otp: code,
      });
      const { token, data } = response;
      Cookies.set("authToken", token, { expires: 7 });
      Cookies.set("userData", JSON.stringify(data.user), { expires: 7 });
      setCurrentUser(data.user);
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong!",
      );
    }
  };

  const verifyPhoneRegister = async (phone, code) => {
    try {
      const response = await verifyPhoneRegisterMutation.mutateAsync({
        phone,
        otp: code,
      });
      const { token, data } = response;
      Cookies.set("authToken", token, { expires: 7 });
      Cookies.set("userData", JSON.stringify(data.user), { expires: 7 });
      setCurrentUser(data.user);
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Something went wrong!",
      );
    }
  };

  const logOut = () => {
    Cookies.remove("authToken");
    Cookies.remove("userData");
    setCurrentUser(null);
  };

  const updateUserData = async (userData) => {
    try {
      const updatedUser = await updateUserMutation.mutateAsync(userData);
      Cookies.set("userData", JSON.stringify(updatedUser.data.updatedUser), {
        expires: 7,
      });
      setCurrentUser(updatedUser.data.updatedUser);
      return { success: true, message: "User data updated successfully!" };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        logIn,
        logOut,
        signUp,
        updateUserData,
        loginWithPhone,
        verifyPhone,
        signUpWithPhone,
        verifyPhoneRegister,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
